/**
 * BetaTerms.jsx — Post-registration T&C acceptance page
 * ======================================================
 *
 * Rendered at `/beta-terms`. Users land here:
 *   * Immediately after registration (before onboarding)
 *   * On next login if their tc_acceptance is missing or outdated
 *     (when admin `force_tc_update = true`)
 *
 * Flow:
 *   1. Fetch server-authoritative doc from GET /api/auth/tc-current-document
 *   2. Render markdown, gate by scroll-to-end
 *   3. Reveal 6 checkboxes after scroll
 *   4. On all checked → "I AGREE" enables
 *   5. POST /api/auth/accept-terms { checkboxes }
 *   6. On success → navigate to /onboarding
 */
import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { authAxios, invalidateMeCache } from "../utils/auth";
import { resolveLoginDestination } from "../utils/onboardingTrigger";
import { Button } from "../components/ui/button";
import { Toaster, toast } from "sonner";
import { FileText, Loader2 } from "lucide-react";

// ==================== SIMPLE MARKDOWN RENDERER ====================
const inlineFormat = (text) => {
  let out = text;
  out = out.replace(/\*\*(.+?)\*\*/g, '<strong class="text-white">$1</strong>');
  out = out.replace(/\*(.+?)\*/g, '<em>$1</em>');
  out = out.replace(/\[(.+?)\]\((.+?)\)/g, '<a href="$2" class="text-divine-gold underline">$1</a>');
  return out;
};

const renderMarkdown = (text) => {
  const lines = text.split("\n");
  const elements = [];
  let inTable = false;
  let tableRows = [];
  const flushTable = () => {
    if (tableRows.length > 0) {
      elements.push(
        <table key={`table-${elements.length}`} className="w-full border-collapse my-4 text-sm">
          <tbody>
            {tableRows.map((row, ri) => (
              <tr key={ri} className="border-b border-white/10">
                {row.map((cell, ci) => (
                  <td key={ci} className={`py-2 px-3 ${ci === 0 ? "font-semibold text-white/80 w-1/3" : "text-white/60"}`}>
                    <span dangerouslySetInnerHTML={{ __html: inlineFormat(cell) }} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      );
      tableRows = [];
    }
    inTable = false;
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (line.startsWith("|") && line.endsWith("|")) {
      const cells = line.split("|").filter(Boolean).map(c => c.trim());
      if (cells.every(c => /^[-:]+$/.test(c))) continue;
      inTable = true;
      tableRows.push(cells);
      continue;
    }
    if (inTable) flushTable();
    if (line.startsWith("## ")) {
      elements.push(<h2 key={i} className="text-lg font-bold text-white mt-6 mb-3">{line.slice(3)}</h2>);
    } else if (line.startsWith("# ")) {
      elements.push(<h1 key={i} className="text-xl font-bold text-white mt-6 mb-3">{line.slice(2)}</h1>);
    } else if (line.startsWith("---")) {
      elements.push(<hr key={i} className="border-white/10 my-6" />);
    } else if (line.trim() === "") {
      elements.push(<div key={i} className="h-2" />);
    } else {
      elements.push(
        <p key={i} className="text-white/70 text-sm leading-relaxed mb-1">
          <span dangerouslySetInnerHTML={{ __html: inlineFormat(line) }} />
        </p>
      );
    }
  }
  if (inTable) flushTable();
  return elements;
};

const BetaTerms = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [agreementText, setAgreementText] = useState("");
  const [version, setVersion] = useState("");
  const [checkboxDefs, setCheckboxDefs] = useState([]);
  const [checkboxes, setCheckboxes] = useState({});
  const [hasScrolledToEnd, setHasScrolledToEnd] = useState(false);
  const scrollContainerRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await authAxios.get(`/auth/tc-current-document`);
        setAgreementText(data.agreement_text);
        setVersion(data.agreement_version);
        setCheckboxDefs(data.checkboxes || []);
      } catch (err) {
        toast.error(err.response?.data?.detail || "Failed to load Beta T&C");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleScroll = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
    if (atBottom && !hasScrolledToEnd) setHasScrolledToEnd(true);
  }, [hasScrolledToEnd]);

  const allChecked = checkboxDefs.length > 0 && checkboxDefs.every(cb => checkboxes[cb.id] === true);
  const canAgree = hasScrolledToEnd && allChecked;

  const handleAgree = async () => {
    if (!canAgree) return;
    setSubmitting(true);
    try {
      await authAxios.post(`/auth/accept-terms`, { checkboxes });
      // Drop the cached tc-status snapshot. Without this, App.js's
      // useEffect re-runs on the post-navigate pathname change, reads
      // the STALE `needs_reprompt: true` from cache, and bounces the
      // user right back to /beta-terms — causing the infinite reload
      // the user reported. invalidateMeCache also clears the tc cache.
      invalidateMeCache();
      toast.success("Terms accepted. Welcome to the Beta.");
      // Send returning users straight to /dashboard when they already
      // have a profile + at least one match; only route fresh accounts
      // through the onboarding wizard. Resolving BEFORE navigate avoids
      // Dashboard mounting for wizard-bound users (kills the news-popup
      // phantom view-count race).
      const dest = await resolveLoginDestination("/dashboard");
      navigate(dest, { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save acceptance. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-divine-black text-white flex items-center justify-center p-2 sm:p-4">
      <Toaster position="top-center" richColors />
      <div className="w-full max-w-3xl h-[calc(100vh-16px)] sm:h-auto sm:max-h-[90vh] flex">
        <div className="bg-divine-charcoal border border-divine-gold/30 rounded-lg overflow-hidden flex flex-col w-full sm:max-h-[90vh]">
          {/* Header */}
          <div className="px-4 sm:px-6 pt-4 sm:pt-6 pb-3 shrink-0 border-b border-divine-gold/10">
            <h1 className="text-divine-gold flex items-center gap-2 text-base sm:text-lg font-cinzel" data-testid="beta-terms-title">
              <FileText className="w-4 h-4 sm:w-5 sm:h-5" />
              <span className="leading-tight">Terms & Conditions — NDA & Beta Terms of Use</span>
              {version && <span className="ml-2 text-xs text-gray-500 hidden sm:inline">v{version}</span>}
            </h1>
            <p className="text-gray-400 text-xs sm:text-sm mt-1">
              Please read the entire agreement carefully. You must scroll to the end to proceed.
            </p>
          </div>

          <div className="flex flex-col flex-1 overflow-hidden px-4 sm:px-6 pb-4 sm:pb-6 min-h-0">
            {/* Scrollable T&C content */}
            <div
              ref={scrollContainerRef}
              onScroll={handleScroll}
              data-testid="beta-terms-scroll-container"
              className="flex-1 min-h-[120px] overflow-y-auto border border-white/10 bg-black/30 rounded-lg p-3 sm:p-4 my-3 sm:my-4"
            >
              {loading ? (
                <div className="flex items-center justify-center py-16 text-gray-400">
                  <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading agreement…
                </div>
              ) : (
                renderMarkdown(agreementText)
              )}
            </div>

            {/* Scroll indicator */}
            {!loading && !hasScrolledToEnd && (
              <p data-testid="beta-terms-scroll-indicator" className="text-center text-xs text-yellow-400/80 mb-2 sm:mb-3 animate-pulse shrink-0">
                Scroll down to read the full agreement
              </p>
            )}

            {/* Checkboxes — visible after scrolling.
                shrink-0 keeps them at natural height so the button never leaves the viewport;
                max-h + overflow-auto lets them scroll internally on very small screens instead
                of pushing the CTA below the fold. */}
            {!loading && hasScrolledToEnd && (
              <div
                data-testid="beta-terms-checkboxes"
                className="space-y-1.5 sm:space-y-2 mb-3 sm:mb-4 shrink-0 max-h-[45vh] overflow-y-auto pr-1"
              >
                {checkboxDefs.map(cb => (
                  <label
                    key={cb.id}
                    data-testid={`beta-terms-checkbox-${cb.id}`}
                    className="flex items-start gap-3 cursor-pointer group p-1.5 sm:p-2 rounded-lg hover:bg-white/5 transition-colors"
                  >
                    <input
                      type="checkbox"
                      checked={!!checkboxes[cb.id]}
                      onChange={e => setCheckboxes(prev => ({ ...prev, [cb.id]: e.target.checked }))}
                      className="mt-0.5 w-4 h-4 rounded shrink-0 accent-divine-gold"
                    />
                    <span className="text-white/70 text-xs leading-relaxed group-hover:text-white/90 transition-colors">
                      {cb.label}
                    </span>
                  </label>
                ))}
              </div>
            )}

            <div className="flex gap-3 shrink-0">
              <Button
                data-testid="beta-terms-agree-btn"
                onClick={handleAgree}
                disabled={!canAgree || submitting}
                className={`w-full ${canAgree ? "bg-divine-gold text-black hover:bg-divine-gold/85" : "bg-gray-600 text-gray-400 cursor-not-allowed"}`}
              >
                {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</> : "I AGREE"}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BetaTerms;
